import { Check, ChevronsUpDown, X } from "lucide-react";
import { useRef, useState } from "react";
import { useQuery } from "urql";

import { Button } from "@/components/ui/button";
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { graphql } from "@/gen/gql";
import useDebounce from "@/hooks/useDebounce";
import { useSearchPlayers } from "@/hooks/useSearchPlayers";
import { cn, cx } from "@/utils/cx";

const playerQuery = graphql(`
    query PlayerQueryById($id: UUID!) {
        player(id: $id) {
            id
            name
            fideId
        }
    }
`);

function usePlayerId(playerId: string | null | undefined) {
    const [result] = useQuery({
        query: playerQuery,
        variables: {
            id: playerId!,
        },
        pause: !playerId || playerId === "",
        requestPolicy: "cache-and-network",
    });

    return playerId && playerId !== "" ? result.data?.player : null;
}

export const PlayerIdInput = ({
    playerId,
    onPlayerIdChange,
    className,
}: {
    playerId: string | null | undefined;
    onPlayerIdChange: (playerId: string | null) => void;
    className?: string;
}) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const [open, setOpen] = useState(false);

    const selectedPlayer = usePlayerId(playerId);

    const [query, setQuery] = useState("");
    const debouncedQuery = useDebounce(query, 500);

    const { players } = useSearchPlayers({
        query: debouncedQuery,
        currentPage: 0,
        pause: !debouncedQuery,
    });
    const clearInput = () => {
        if (inputRef.current) {
            inputRef.current.value = "";
        }
        onPlayerIdChange(null);
        setQuery("");
    };

    return (
        <div className={cx("flex flex-row gap-1", className)}>
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={open}
                        className="flex-1 justify-between"
                    >
                        {playerId && playerId !== "" && selectedPlayer
                            ? `${selectedPlayer.name}${selectedPlayer.fideId ? ` (${selectedPlayer.fideId})` : ""})`
                            : "Find player..."}
                        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[300px] p-0">
                    <Command shouldFilter={false}>
                        <CommandInput
                            placeholder="Search for players..."
                            value={query}
                            onInput={e => setQuery(e.currentTarget.value)}
                        />
                        <CommandList>
                            <CommandEmpty>No matching players found.</CommandEmpty>
                            <CommandGroup>
                                {players.map(player => (
                                    <CommandItem
                                        key={player.id}
                                        value={player.id}
                                        onSelect={currentValue => {
                                            onPlayerIdChange(
                                                playerId === currentValue ? null : currentValue
                                            );
                                            setOpen(false);
                                        }}
                                    >
                                        <Check
                                            className={cn(
                                                "mr-2 h-4 w-4",
                                                playerId === player.id ? "opacity-100" : "opacity-0"
                                            )}
                                        />
                                        {player.name} ({player.fideId})
                                    </CommandItem>
                                ))}
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>
            {playerId && playerId !== "" && (
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-10 px-2"
                    onClick={clearInput}
                    aria-label="Clear selected player"
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
        </div>
    );
};
